/* eslint-disable react/no-unstable-nested-components */
import {
    Badge,
    Button,
    Card,
    // Checkbox,
    DatePicker,
    FilterButton,
    Pagination,
    Popover,
    PopoverContent,
    PopoverTrigger,
    SearchInput,
    Table,
} from "@jhool-io/fe-components";
import * as React from "react";
import { ColumnDef } from "@tanstack/react-table";
import { format } from "date-fns";
import { Link, useSearchParams } from "react-router-dom";

import patternBg from "../../../../assets/images/patternbg.png";
import { ExpiringPolicy, ExpiryDateRange } from "../../types/dashboard.types";
import {
    cn,
    displayNameInRightFormat,
    formatDate,
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
} from "../../../../utils/helpers";
import { momentNotTz } from "../../../../utils/moment";
import { useDebounce } from "../../../../hooks/helpers";
import { FinancialAssistanceStatus } from "../../../../utils/types/client";
import { useFetchExpiringPolicies } from "../../hooks/dashboard.queries";
import ListState from "../../../../components/ListState/ListState";

import Loader from "../../../../shared-ui/Loader/Loader";
import { getInsuranceProviderIdsFilter } from "../../../../shared-ui/InsuranceProviderFilter/InsuranceProviderFilter";

export default function ExpiringPoliciesList() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [startDate, setStartDate] = React.useState<Date | null>(null);
    const [endDate, setEndDate] = React.useState<Date | null>(null);
    const [searchString, setSearchString] = React.useState<string>("");
    const searchFilter = useDebounce(searchString, 500);

    const [sortConfig, setSortConfig] = React.useState<{
        key: string;
        direction: string;
    }>({
        key: "accumulations_end_date",
        direction: "DESC",
    });

    const customDateFilter = searchParams.get("expiry_date") || "this_month";

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onChange = (dates: any) => {
        const [start, end] = dates;
        setStartDate(start);
        setEndDate(end);
        searchParams.delete("expiry_date");
        setSearchParams(searchParams);
    };

    const sortableColumns = [
        "financial_assistance_end_date",
        "accumulations_start_date",
        "accumulations_end_date",
    ];

    const handleConfigAttribute = () => {
        if (sortableColumns.includes(sortConfig.key)) {
            return sortConfig.key;
        }

        return undefined;
    };

    const startDateFilter = startDate
        ? format(startDate, "yyyy-MM-dd")
        : undefined;
    const endDateFilter = endDate ? format(endDate, "yyyy-MM-dd") : undefined;
    const insuranceProviderIdsFilter =
        getInsuranceProviderIdsFilter(searchParams);

    const pageFilter = Number(searchParams.get("page") || 1);
    const limitFilter = Number(searchParams.get("limit")) || 20;
    const { data, isSuccess, isLoading, error } = useFetchExpiringPolicies({
        search_string: searchFilter,
        custom_date: customDateFilter,
        from_date: startDateFilter,
        to_date: endDateFilter,
        page: pageFilter,
        limit: limitFilter,
        insurance_provider_ids: insuranceProviderIdsFilter,
        sort_attribute:
            searchParams.get("sort_attr") || handleConfigAttribute(),
        sort_order: searchParams.get("sort_order") || sortConfig.direction,
    });

    const columns: ColumnDef<ExpiringPolicy>[] = React.useMemo(
        () => [
            // {
            //     id: "select",
            //     header: ({ table }) => (
            //         <div className="mt-[4px]">
            //             <Checkbox
            //                 handleChange={table.getToggleAllRowsSelectedHandler()}
            //                 indeterminate={table.getIsSomeRowsSelected()}
            //                 label=""
            //                 name="row"
            //                 value="row"
            //                 isChecked={table.getIsAllRowsSelected()}
            //             />
            //         </div>
            //     ),
            //     cell: ({ row }) => (
            //         <Checkbox
            //             handleChange={row.getToggleSelectedHandler()}
            //             indeterminate={row.getIsSomeSelected()}
            //             label=""
            //             name="row"
            //             value="row"
            //             isChecked={row.getIsSelected()}
            //         />
            //     ),
            // },
            {
                accessorKey: "client_name",
                header: "Client",
                cell: ({ row }) => (
                    <>
                        <Link
                            to={`/clients/${row.original.client_id}`}
                            className="text-textLink underline capitalize"
                        >
                            {displayNameInRightFormat(row.original.client_name)}
                        </Link>
                        {row.original.financial_assistance_status ===
                            FinancialAssistanceStatus.YES && (
                            <>
                                {" "}
                                <Badge
                                    content="FAP"
                                    className="align-middle"
                                    type="info"
                                />
                            </>
                        )}
                        {row.original.financial_assistance_status ===
                            FinancialAssistanceStatus.EXPIRED && (
                            <>
                                {" "}
                                <Badge
                                    content="FAP expired"
                                    className="align-middle"
                                    type="danger"
                                />
                            </>
                        )}
                    </>
                ),
            },
            {
                accessorKey: "date_of_birth",
                header: "DATE OF BIRTH",
                cell: ({ row }) => (
                    <span className="capitalize">
                        {row.original.date_of_birth
                            ? `${formatDate(
                                  `${row.original.date_of_birth}T00:00:00`,
                                  true
                              )}`
                            : "--"}
                    </span>
                ),
            },

            {
                accessorKey: "accumulations_start_date",
                header: "ACCUMULATION START DATE",
                cell: ({ row }) =>
                    row.original.accumulations_start_date
                        ? formatDate(
                              row.original.accumulations_start_date,
                              true
                          )
                        : "--",
            },
            {
                accessorKey: "accumulations_end_date",
                header: "ACCUMULATION END DATE",
                cell: ({ row }) =>
                    row.original.accumulations_end_date
                        ? formatDate(row.original.accumulations_end_date, true)
                        : "--",
            },
            {
                accessorKey: "policy_effective_end_date",
                header: "POLICY END DATE",
                cell: ({ row }) =>
                    row.original.policy_effective_end_date
                        ? formatDate(
                              row.original.policy_effective_end_date,
                              true
                          )
                        : "--",
            },
            {
                accessorKey: "insurance_provider",
                header: "INSURANCE PROVIDER",
                cell: ({ row }) => (
                    <span className="capitalize">
                        {row.original.insurance_provider}
                    </span>
                ),
            },

            {
                accessorKey: "employer_name",
                header: "EMPLOYER NAME",
                cell: ({ row }) => (
                    <span className="capitalize">
                        {row.original.employer_name}
                    </span>
                ),
            },
            {
                accessorKey: "insurance_member_id",
                header: "INSURACE MEMBER ID",
            },
            {
                accessorKey: "financial_assistance_end_date",
                header: "FAP end date",
                cell: ({ row }) =>
                    row.original.financial_assistance_end_date
                        ? formatDate(
                              row.original.financial_assistance_end_date,
                              true
                          )
                        : "--",
            },
            // {
            //     accessorKey: "more",
            //     header: "",
            //     cell: () => (
            //         <Dropdown>
            //             <DropdownTrigger>
            //                 <Button
            //                     variant="normal"
            //                     size="auto"
            //                     className="w-16 h-16 rounded-[16px] shadow-morebtn"
            //                 >
            //                     <MoreIcon />
            //                 </Button>
            //             </DropdownTrigger>
            //             <DropdownContent
            //                 width="auto"
            //                 className="dd-modal data-[data-radix-popper-content-wrapper]:z-[200]"
            //             >
            //                 <Button size="auto" variant="normal">
            //                     Mark as complete
            //                 </Button>
            //             </DropdownContent>
            //         </Dropdown>
            //     ),
            // },
        ],
        []
    );

    const customDateForSelect = [
        ...Object.values(ExpiryDateRange).map((item) => {
            return item;
        }),
    ];

    const handleCustomDateChange = (range: string) => {
        searchParams.set("expiry_date", range);
        setStartDate(null);
        setEndDate(null);
        setSearchParams(searchParams);
    };

    // onChange handler for page
    const handlePageChange = (page: number) => {
        searchParams.set("page", String(page));
        setSearchParams(searchParams);
    };

    const handleLimitChange = (limit: number) => {
        searchParams.set("limit", String(limit));
        setSearchParams(searchParams);
    };

    return (
        <div>
            <Card classNames="bg-[--card-bg-gray] mb-[--spacing-header]">
                <div className="flex items-center justify-between mb-[--spacing-items] flex-wrap gap-[--spacing-items]">
                    <h3 className="text-title font-bold">Expiring Policies</h3>
                    <Popover>
                        <PopoverTrigger asChild>
                            <FilterButton>
                                {startDateFilter != null &&
                                endDateFilter !== null ? (
                                    <>
                                        {momentNotTz(startDateFilter).format(
                                            "MMM DD"
                                        )}{" "}
                                        -{" "}
                                        {momentNotTz(endDateFilter).format(
                                            "MMM DD"
                                        )}
                                    </>
                                ) : (
                                    <span>
                                        {makeStringFirstLetterCapital(
                                            removeEnumUnderscore(
                                                customDateFilter
                                            )
                                        )}
                                    </span>
                                )}
                            </FilterButton>
                        </PopoverTrigger>
                        <PopoverContent
                            align="end"
                            className="h-auto w-auto z-[50]"
                        >
                            <div className="flex gap-y-3">
                                <div className="flex flex-col gap-x-[10px] mt-1.5 w-auto items-start">
                                    {customDateForSelect.map((range) => (
                                        <Button
                                            variant="normal"
                                            onClick={() =>
                                                handleCustomDateChange(range)
                                            }
                                            className={cn({
                                                "text-primary":
                                                    customDateFilter === range,
                                            })}
                                            key={range}
                                        >
                                            {makeStringFirstLetterCapital(
                                                removeEnumUnderscore(range)
                                            )}
                                        </Button>
                                    ))}
                                </div>
                                <div>
                                    <DatePicker
                                        selected={startDate}
                                        onChange={onChange}
                                        startDate={startDate}
                                        endDate={endDate}
                                        canSelectRange
                                        additionalCalendarClass="shadow-none"
                                        inline
                                    />
                                </div>
                            </div>
                        </PopoverContent>
                    </Popover>
                </div>
                <div className="grid grid-cols-2 max-[768px]:grid-cols-1 gap-[--spacing-items]">
                    <Card classNames="flex flex-col gap-y-2 justify-center">
                        <span className="uppercase text-caption font-medium">
                            Duration
                        </span>

                        <span className="text-title font-bold">
                            {startDateFilter != null &&
                            endDateFilter !== null ? (
                                <>
                                    {momentNotTz(startDateFilter).format(
                                        "MMM DD"
                                    )}{" "}
                                    -{" "}
                                    {momentNotTz(endDateFilter).format(
                                        "MMM DD"
                                    )}
                                </>
                            ) : (
                                <span>
                                    {makeStringFirstLetterCapital(
                                        removeEnumUnderscore(customDateFilter)
                                    )}
                                </span>
                            )}
                        </span>
                        <img
                            src={patternBg}
                            alt="pattern of boxes"
                            className="absolute right-[8px] h-[80px] w-[95px] -translate-y-1/2 top-1/2"
                        />
                    </Card>
                    <Card classNames="flex flex-col gap-y-2 justify-center">
                        <span className="uppercase text-caption font-medium">
                            TOTAL POLICIES EXPIRING THIS PERIOD
                        </span>

                        <span className="text-title font-bold">
                            {data?.total_count || 0}
                        </span>
                        <img
                            src={patternBg}
                            alt="pattern of boxes"
                            className="absolute right-[8px] h-[80px] w-[95px] -translate-y-1/2 top-1/2"
                        />
                    </Card>
                </div>
            </Card>
            <div className="mb-[--spacing-items]">
                <SearchInput
                    containerClass="w-[400px]"
                    onChange={(e) => setSearchString(e.target.value)}
                    placeholder="Search by client name or Insurance member ID"
                />
            </div>
            {error && error?.response?.status !== 404 && (
                <ListState
                    isError
                    stateHelperText="Try reloading this page to solve this issue"
                    errorMsg={
                        error?.response?.data.message ||
                        `Cannot display expiring policies at this time please try again later`
                    }
                />
            )}
            {data && isSuccess && data?.data?.length === 0 && (
                <ListState
                    stateHelperText="Expiring policy list will appear here"
                    isError={false}
                    emptyMessage="There are no expiring policies"
                />
            )}
            {isLoading && <Loader />}

            {data && data?.data?.length > 0 && isSuccess && !isLoading && (
                <Table
                    columns={columns}
                    data={data?.data}
                    setSortConfig={setSortConfig}
                    sortConfig={sortConfig}
                    sortableColumns={sortableColumns}
                    hasPagination={data.total_count > 20}
                    pagination={
                        <Pagination
                            totalCount={data.total_count}
                            totalPages={data.total_pages}
                            currentPage={pageFilter}
                            onPageChange={handlePageChange}
                            onLimitChange={handleLimitChange}
                        />
                    }
                />
            )}
        </div>
    );
}
